import { type FC } from 'react';
import classnames from 'classnames';
import { useFragment, graphql } from 'react-relay';
import { defineMessages, useIntl } from 'dibs-react-intl';
import { shortAddress } from 'dibs-crypto-utils/exports/formattingUtils.ts';
import { Link } from 'dibs-elements/exports/Link.tsx';
import { NftAvatarCircle } from 'dibs-crypto-utils/exports/NftAvatarCircle.ts';
import { type NftCreatorTile_nftCreator$key } from './__generated__/NftCreatorTile_nftCreator.graphql.ts';

import styles from './NftCreatorTile.scss';

type Props = {
    nftCreator: NftCreatorTile_nftCreator$key | null | undefined;
    isSearch: boolean;
};

const messages = defineMessages({
    creationCount: {
        id: 'nft.creators.creatorTile.nftItemCount',
        defaultMessage: '{nftItemCount, plural, one {# Creation} other {# Creations}}',
    },
});

export const NftCreatorTile: FC<Props> = ({ isSearch, nftCreator: nftCreatorRef }) => {
    const { formatMessage } = useIntl();
    const nftCreator = useFragment(
        graphql`
            fragment NftCreatorTile_nftCreator on NftCreatorsSearchResult {
                address {
                    ...NftAvatarCircle_address
                    address
                    publicProfile {
                        username
                        profileName
                        bio
                        heroImage {
                            path
                        }
                    }
                    publicProfileUrl
                }
                nftItemCount
            }
        `,
        nftCreatorRef
    );
    const { nftItemCount } = nftCreator || {};
    const { publicProfile, address, publicProfileUrl } = nftCreator?.address || {};

    if (!publicProfile) {
        return null;
    }
    return (
        <li className={styles.gridItem}>
            <Link href={publicProfileUrl || ''} buyerLinkType="standardLinkNoEffect">
                {isSearch ? (
                    <div className={styles.searchContainer}>
                        <NftAvatarCircle
                            address={nftCreator?.address}
                            wrapperClassName={classnames(
                                styles.avatarSearchContainer,
                                styles.important
                            )}
                            avatarSize={40}
                            loading="lazy"
                        />
                        <div className={styles.displayNameSearchContainer}>
                            {publicProfile.profileName && (
                                <p className={styles.profileNameSearch}>
                                    {publicProfile.profileName}
                                </p>
                            )}
                            {nftItemCount && (
                                <p className={styles.nftItemCountSearch}>
                                    {formatMessage(messages.creationCount, { nftItemCount })}
                                </p>
                            )}
                        </div>
                    </div>
                ) : (
                    <div className={styles.container}>
                        <div className={styles.bannerContainer}>
                            <div className={styles.heroContainer}>
                                {!!publicProfile.heroImage?.path && (
                                    <img
                                        loading="lazy"
                                        className={styles.heroImage}
                                        src={publicProfile.heroImage.path}
                                        alt={`hero-${publicProfile.profileName}`}
                                    />
                                )}
                            </div>
                            <NftAvatarCircle
                                address={nftCreator?.address}
                                wrapperClassName={classnames(
                                    styles.avatarContainer,
                                    styles.important
                                )}
                                avatarSize={70}
                                loading="lazy"
                            />
                        </div>
                        <div className={styles.profileContainer}>
                            <p className={styles.username}>
                                {publicProfile.username
                                    ? `@${publicProfile.username}`
                                    : shortAddress(address || '')}
                            </p>
                            <div className={styles.displayNameItemCountContainer}>
                                {publicProfile.profileName && <p>{publicProfile.profileName}</p>}
                                {nftItemCount && publicProfile.profileName && <span>|</span>}
                                {nftItemCount && (
                                    <p className={styles.nftItemCount}>
                                        {formatMessage(messages.creationCount, { nftItemCount })}
                                    </p>
                                )}
                            </div>
                            <p className={styles.bio}>{publicProfile.bio}</p>
                        </div>
                    </div>
                )}
            </Link>
        </li>
    );
};
